import { useCallback, useEffect, useRef, useState } from "react";
import { createReadiness, resetReadiness, stepReadiness, tickColor, TICK_COUNT } from "./readiness";
import { INITIAL_RESULT, TOTAL_CHECKS, createFaceChecker, sameResult } from "./faceChecks";

const ANALYZE_EVERY_MS = 80;
const MANUAL_FALLBACK_MS = 7000; // never leave anyone stuck: allow manual capture after this

export const useRefScan = () => {
  const videoRef = useRef(null), tickRefs = useRef([]), flashRef = useRef(null);
  const [cam, setCam] = useState("starting");
  const [result, setResult] = useState(INITIAL_RESULT);
  const [ready, setReady] = useState(false);
  const [review, setReview] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [allowManual, setAllowManual] = useState(false);
  const readiness = useRef(createReadiness());
  const latest = useRef(INITIAL_RESULT), checker = useRef(null);
  const reviewRef = useRef(false), readyRef = useRef(false), manualRef = useRef(false);

  const enableManual = () => { manualRef.current = true; setAllowManual(true); };

  useEffect(() => {
    let stream = null, cancelled = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", aspectRatio: { ideal: 0.75 }, width: { ideal: 1080 }, height: { ideal: 1440 } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = videoRef.current; if (!v) return;
        v.srcObject = stream; await v.play(); setCam("live");
      } catch (e) { if (!cancelled) setCam(e?.name === "NotAllowedError" ? "denied" : "unavailable"); }
    })();
    return () => { cancelled = true; stream?.getTracks().forEach((t) => t.stop()); };
  }, []);

  useEffect(() => {
    let alive = true, raf = 0, lastRun = 0;
    createFaceChecker().then((c) => { if (alive) checker.current = c; else c.close(); }).catch(() => enableManual());
    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      const v = videoRef.current, c = checker.current;
      if (!c || !v || v.readyState < 2 || reviewRef.current || now - lastRun < ANALYZE_EVERY_MS) return;
      lastRun = now;
      const r = c.analyze(v, now); latest.current = r;
      setResult((prev) => (sameResult(prev, r) ? prev : r));
    };
    raf = requestAnimationFrame(loop);
    return () => { alive = false; cancelAnimationFrame(raf); checker.current?.close(); checker.current = null; };
  }, []);

  useEffect(() => {
    let raf = 0, last = 0;
    const loop = (t) => {
      raf = requestAnimationFrame(loop);
      const dt = last ? t - last : 16; last = t;
      if (reviewRef.current) return;
      const s = readiness.current;
      stepReadiness(s, latest.current.passing, TOTAL_CHECKS, dt);
      for (let k = 0; k < TICK_COUNT; k++) tickRefs.current[k]?.setAttribute("stroke", tickColor(s, k));
      if (s.ready !== readyRef.current) { readyRef.current = s.ready; setReady(s.ready); }
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (cam !== "live" || review) return;
    const id = setTimeout(enableManual, MANUAL_FALLBACK_MS);
    return () => clearTimeout(id);
  }, [cam, review]);

  const capture = useCallback(() => {
    const v = videoRef.current;
    if ((!readiness.current.ready && !manualRef.current) || reviewRef.current || !v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d")?.drawImage(v, 0, 0);
    reviewRef.current = true; setReview(true);
    const f = flashRef.current;
    if (f) { f.classList.remove("is-go"); void f.offsetWidth; f.classList.add("is-go"); }
    c.toBlob((blob) => { if (blob) setPhoto({ blob, url: URL.createObjectURL(blob) }); }, "image/jpeg", 0.92);
  }, []);

  useEffect(() => {
    if (!ready || review) return;
    const id = setTimeout(() => capture(), 300);
    return () => clearTimeout(id);
  }, [ready, review, capture]);

  const retake = useCallback(() => {
    setPhoto(null); resetReadiness(readiness.current); latest.current = INITIAL_RESULT;
    checker.current?.reset(); setResult(INITIAL_RESULT);
    readyRef.current = false; setReady(false); reviewRef.current = false; setReview(false);
    manualRef.current = false; setAllowManual(false);
  }, []);

  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.url); }, [photo]);

  return { videoRef, tickRefs, flashRef, cam, result, ready, review, photo, allowManual, capture, retake };
};
